import { Preferences } from '@capacitor/preferences'

import { api } from './api'

/**
 * Offline-first queue of exercise-run uploads.
 *
 * The training screen always calls {@link enqueueRun} at session end (or
 * abort). The local persistence is the source of truth — uploading is
 * opportunistic. A separate flusher walks the queue when the app boots,
 * comes back to the foreground, or the device transitions back online.
 *
 * The backend's idempotency guard (see
 * `app/Http/Controllers/Patient/ExerciseRunController.php`) keys on
 * `client_run_id`, so a retry after a partial-network "did the POST land?"
 * outage will not double-write. Failed 4xx other than 409 are dropped from
 * the queue with a console.warn — payloads that the server rejects today
 * will keep being rejected tomorrow, so the data isn't recoverable and
 * keeping a poison entry in the queue forever is worse than losing it.
 *
 * Storage: a single key in `@capacitor/preferences` holding the full JSON
 * queue array. We don't worry about concurrency — the patient app is
 * single-instance and a single render context.
 */

const STORAGE_KEY = 'lingotalk.exercise_runs.queue'

export interface ExerciseRunHelpEntry {
  kind: 'mouth' | 'cloud' | 'ear' | 'ab'
  level: 1 | 2 | 3
  t_offset_ms: number
  /**
   * True if the patient activated 0.5× slow-mode at any point during
   * this help play. Today this is only ever set for `kind === 'mouth'`
   * (the only help with a fullscreen overlay carrying the snail toggle).
   * Optional + defaults to false for backwards compatibility — runs
   * uploaded before this field existed have no `slow` and the popover
   * treats that as `false`.
   */
  slow?: boolean
}

export interface ExerciseRunItemPayload {
  item_id: number
  position: number
  attempt_count: number
  was_correct: boolean
  was_revealed: boolean
  helps_used: ExerciseRunHelpEntry[]
  duration_ms: number
}

export interface ExerciseRunPayload {
  client_run_id: string
  started_at: string
  completed_at: string | null
  items_correct: number
  items_failed: number
  duration_ms: number
  rating_mode: 'speech_recognition' | 'self_assessment' | 'none'
  status: 'completed' | 'abandoned'
  items: ExerciseRunItemPayload[]
}

export interface QueuedRun {
  /** Mirrors `payload.client_run_id` — duplicated at the top level so the
   *  flusher doesn't have to deref the payload for dedup. */
  client_run_id: string
  exercise_id: number
  /**
   * Set only for therapist (web embed) runs of an *assigned* exercise. When
   * present the flusher uploads to the assignment-scoped therapist endpoint
   * (`/therapist/patients/{id}/assignments/{exercise}/runs`) instead of the
   * native patient endpoint. The value is the LingoTalk patient id.
   */
  therapist_patient_id?: string
  /**
   * Set with `therapist_patient_id` when the therapist ran the patient's OWN
   * exercise rather than an assigned one. Flips the upload to the
   * patient-authored endpoint (`/therapist/patients/{id}/exercises/{exercise}/runs`),
   * where `exercise_id` is a PATIENT exercise id — a different id space from
   * the assignment endpoint's, which is why this needs its own flag rather
   * than being inferred.
   */
  patient_authored?: boolean
  /**
   * Set only for patient-app runs of a homework exercise (a therapist exercise
   * the patient was assigned as homework). When true the flusher uploads to the
   * homework endpoint (`/patient/homework/{exercise}/runs`) — `exercise_id` is
   * then the THERAPIST exercise id. Mutually exclusive with `therapist_patient_id`.
   */
  homework?: boolean
  payload: ExerciseRunPayload
  attempt_count: number
  last_error?: string
  enqueued_at: string
}

// --- queue I/O ----------------------------------------------------------

async function readQueue(): Promise<QueuedRun[]> {
  const { value } = await Preferences.get({ key: STORAGE_KEY })
  if (value === null || value === undefined) return []
  try {
    const parsed = JSON.parse(value)
    return Array.isArray(parsed) ? (parsed as QueuedRun[]) : []
  } catch {
    return []
  }
}

async function writeQueue(queue: QueuedRun[]): Promise<void> {
  await Preferences.set({ key: STORAGE_KEY, value: JSON.stringify(queue) })
}

export async function listQueued(): Promise<QueuedRun[]> {
  return readQueue()
}

/**
 * Add a run to the queue and (best-effort) try to flush it immediately so
 * a session that completes with network up sees its result in the picker
 * on the next render. If the immediate flush fails the entry stays in the
 * queue and the lifecycle hooks (network change, app resume, app boot)
 * pick it up later.
 */
export async function enqueueRun(
  exerciseId: number,
  payload: ExerciseRunPayload,
  therapistPatientId?: string,
  homework: boolean = false,
  patientAuthored: boolean = false,
): Promise<void> {
  const queue = await readQueue()
  // Defensive dedup: if the same client_run_id already sits in the queue
  // (e.g. user smacked the X twice quickly), the second call is a no-op.
  if (queue.some((q) => q.client_run_id === payload.client_run_id)) return

  queue.push({
    client_run_id: payload.client_run_id,
    exercise_id: exerciseId,
    // Omit these keys entirely on the native own-exercise path so the on-disk
    // shape (and every existing queued entry) is unchanged.
    ...(therapistPatientId !== undefined ? { therapist_patient_id: therapistPatientId } : {}),
    ...(homework ? { homework: true } : {}),
    ...(patientAuthored ? { patient_authored: true } : {}),
    payload,
    attempt_count: 0,
    enqueued_at: new Date().toISOString(),
  })
  await writeQueue(queue)
  void flushQueue()
}

/**
 * Walk the queue and POST each entry. Stops at the first persistent
 * failure to avoid hammering an unhealthy server; transient failures
 * (network / 5xx) leave the entry in place for the next trigger.
 */
export async function flushQueue(): Promise<void> {
  const queue = await readQueue()
  if (queue.length === 0) return

  // We rebuild the queue from the kept entries; mutating in-place would
  // make the on-disk state inconsistent if an await rejects mid-walk.
  const kept: QueuedRun[] = []
  for (const entry of queue) {
    try {
      const url = entry.homework
        ? `/patient/homework/${entry.exercise_id}/runs`
        : entry.therapist_patient_id !== undefined
          ? `/therapist/patients/${entry.therapist_patient_id}/assignments/${entry.exercise_id}/runs`
          : `/patient/exercises/${entry.exercise_id}/runs`
      await api.post(url, entry.payload)
      // 2xx — drop from queue.
    } catch (e) {
      const status = pickAxiosStatus(e)
      if (status === null) {
        // Network/timeout — keep, retry later.
        kept.push({
          ...entry,
          attempt_count: entry.attempt_count + 1,
          last_error: stringifyError(e),
        })
      } else if (status >= 500) {
        kept.push({
          ...entry,
          attempt_count: entry.attempt_count + 1,
          last_error: `HTTP ${status}`,
        })
      } else if (status === 409) {
        // Duplicate — server saw the same client_run_id. Drop.
        // (The controller currently returns 200 for an existing run, so
        // 409 isn't on the active path. Future-proof.)
      } else {
        // 4xx (other than 409) — payload is malformed in a way the server
        // rejects. Keeping it in the queue means an infinite retry loop.
        // Drop with a warn so we have a record.
        console.warn(
          `[run-queue] dropping un-recoverable run ${entry.client_run_id}: HTTP ${status}`,
        )
      }
    }
  }
  await writeQueue(kept)
}

/**
 * Drop a single entry by `client_run_id`. Exposed for tests + an
 * eventual "discard pending uploads" debug button; not used on the
 * happy path.
 */
export async function dropFromQueue(clientRunId: string): Promise<void> {
  const queue = await readQueue()
  const next = queue.filter((q) => q.client_run_id !== clientRunId)
  if (next.length !== queue.length) {
    await writeQueue(next)
  }
}

// --- helpers ------------------------------------------------------------

function pickAxiosStatus(err: unknown): number | null {
  if (err && typeof err === 'object') {
    const response = (err as { response?: { status?: unknown } }).response
    const status = response?.status
    if (typeof status === 'number') return status
  }
  return null
}

function stringifyError(err: unknown): string {
  if (err instanceof Error) return err.message
  try {
    return JSON.stringify(err)
  } catch {
    return String(err)
  }
}
